import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Database, ArrowRight, ArrowDown, Target } from 'lucide-react';
import DemoBanner from '../../components/optimar/DemoBanner';
import SectionBreak from '../../components/optimar/SectionBreak';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OptimarDataMapping = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/optimar/data-mapping`);
        setRows(res.data.rows || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load data mapping');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="space-y-8 sm:space-y-12" data-testid="optimar-data-mapping-page">
      <DemoBanner />

      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
          Aqua Processing Line Demo
        </p>
        <h1
          className="mt-1 text-2xl font-bold leading-tight text-slate-900 sm:text-3xl"
          style={{ fontFamily: 'Manrope' }}
        >
          Data mapping
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-600">
          How SCADA sources, PostgreSQL tables, RFID and QR labels, cartons,
          pallets, waste and the plant report module map onto PureSustain
          concepts. All data on this page is Optimar-inspired demo data.
        </p>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
        </div>
      ) : (
        <>
          <SectionBreak data-testid="optimar-datamapping-break-before-table" />
          {/* Desktop / large-tablet table (≥ lg) */}
          <div
            className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white lg:block"
            data-testid="optimar-data-mapping-table-wrapper"
          >
            <div className="overflow-x-auto">
              <table
                className="min-w-full divide-y divide-slate-200"
                data-testid="optimar-data-mapping-table"
              >
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Source (plant side)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500" />
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      PureSustain target
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50"
                      data-testid={`optimar-mapping-row-${idx}`}
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500">
                        {row.category}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">
                        <div className="flex items-center gap-2">
                          <Database className="h-4 w-4 text-slate-400" />
                          {row.source}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        <ArrowRight className="h-4 w-4" />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-blue-700">
                        {row.target}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {row.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile / tablet stacked cards (< lg) — preserves every column */}
          <div
            className="space-y-4 lg:hidden"
            data-testid="optimar-data-mapping-cards"
          >
            {rows.map((row, idx) => (
              <article
                key={idx}
                data-testid={`optimar-mapping-card-${idx}`}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Category
                </p>
                <p
                  className="mt-0.5 text-sm font-medium text-slate-700"
                  data-testid={`optimar-mapping-card-${idx}-category`}
                >
                  {row.category}
                </p>

                <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Source / Plant side
                </p>
                <div
                  className="mt-0.5 flex items-start gap-2 text-sm font-semibold text-slate-900"
                  data-testid={`optimar-mapping-card-${idx}-source`}
                >
                  <Database className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  <span className="break-words">{row.source}</span>
                </div>

                <div className="mt-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-400">
                  <ArrowDown className="h-4 w-4" />
                  <span>Maps to</span>
                </div>

                <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  PureSustain target
                </p>
                <div
                  className="mt-0.5 flex items-start gap-2 text-sm font-semibold text-blue-700"
                  data-testid={`optimar-mapping-card-${idx}-target`}
                >
                  <Target className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                  <span className="break-words">{row.target}</span>
                </div>

                <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Description
                </p>
                <p
                  className="mt-0.5 text-sm leading-relaxed text-slate-600"
                  data-testid={`optimar-mapping-card-${idx}-description`}
                >
                  {row.description}
                </p>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default OptimarDataMapping;
